import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {object} Tier
 * @property {string} name
 * @property {number} min - Minimum pairs of the tier.
 * @property {number | null} max
 * @property {number} pct - Discount percentage.
 * @property {string} unit - Price per pair, already formatted ("$552").
 * @property {number} cents - Price per pair in cents.
 *
 * @typedef {object} TierData
 * @property {string} from - Price per pair at the highest discount, already formatted ("" when there is none).
 * @property {Tier[]} tiers - Sorted by `min`.
 */

/** Request for the pairs of the cart, shared by the parts of every tiers component of the page. */
let cartPairsRequest;

/** When the shared request started (ms), so that two parts reacting to the same cart change make one request. */
let cartPairsRequestStart = 0;

/**
 * Quantity of every line of the cart, from the cart Ajax API.
 * @returns {Promise<number | undefined>}
 */
function fetchCartItemCount() {
  return fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  })
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
    .then((cart) => (Number.isFinite(Number(cart?.item_count)) ? Number(cart.item_count) : undefined))
    .catch(() => undefined);
}

/**
 * Pairs of the cart that count for wholesale (`sections/pc-cart-pairs.liquid`, rendered by the Section Rendering API).
 * When that section cannot be read (it is not in the theme being previewed, or the request fails) it falls back to the
 * quantity of every line of the cart (`/cart.js`), which only differs when a product has the `sin-mayoreo` tag. Resolves
 * to `undefined` when neither can be read, so the page keeps what it shows.
 * @returns {Promise<number | undefined>}
 */
function fetchCartPairs() {
  if (cartPairsRequest && Date.now() - cartPairsRequestStart < 200) return cartPairsRequest;

  cartPairsRequestStart = Date.now();
  const request = fetch(`${Theme.routes.cart_url}?section_id=pc-cart-pairs`, {
    headers: { Accept: 'text/html' },
    credentials: 'same-origin',
    cache: 'no-store',
  })
    .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
    .then((html) => {
      const match = html.match(/data-pc-cart-pairs="(\d+)"/);
      return match ? Number(match[1]) : undefined;
    })
    .catch(() => undefined)
    .then((pairs) => (pairs === undefined ? fetchCartItemCount() : pairs));

  cartPairsRequest = request;
  return request;
}

/**
 * Wholesale tiers of the product page (`snippets/pc-tiers.liquid`), in two parts that share this component: the table
 * and the status (message, progress bar and cart note) that `blocks/buy-buttons.liquid` draws under the quantity.
 *
 * The tier is decided by the pairs of the whole cart plus the quantity in the input, because the automatic discounts
 * count the cart, not the product: a visitor who has 10 pairs in the cart sees the second row highlighted on every
 * product page. The server renders the state for the initial quantity and the pairs already in the cart
 * (`data-cart-pairs`); this component refreshes that figure when the page loads (the HTML can come from a cache) and
 * after every cart change, from `sections/pc-cart-pairs.liquid`.
 *
 * This component keeps the state in sync, without reloading, with the quantity input (typing and the +/- buttons),
 * the cart and with the selected variant: it highlights the active row, rewrites the
 * message ("Agrega 9 pares más y paga $552 por par."), moves the bar from the start of the current tier to the next
 * one, writes "Agregar al carrito · 12 pares · $6,624" on the add to cart button and updates the "from" price elements
 * (`[data-pc-from-price]`) of the page. Each part works alone: a product without a quantity input (marked "Bajo
 * pedido") only has the table, which shows the tiers for one pair.
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} data - JSON with the tiers of the selected variant.
 * @property {HTMLElement} [status] - Status container; `data-state` is "progress", "applied" or "best".
 * @property {HTMLElement} [line] - Message line (live region).
 * @property {HTMLElement} [message] - The message text.
 * @property {HTMLAnchorElement} [quoteLink] - "Solicita cotización." link, shown when there is no next tier.
 * @property {HTMLElement} [progress] - Progress bar element (role progressbar).
 * @property {HTMLElement} [bar] - Filled part of the progress bar.
 * @property {HTMLElement[]} [rows] - One row per tier, in the same order as the JSON.
 *
 * @extends {Component<Refs>}
 */
class PcTiers extends Component {
  requiredRefs = ['data'];

  /** @type {AbortController | undefined} */
  #abort;

  /** Last quantity shown, kept while the input is empty or invalid mid-typing. */
  #quantity = 0;

  /** Pairs already in the cart that count for wholesale. */
  #cartPairs = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const scope = this.closest('product-component') ?? this.closest('.shopify-section') ?? document;
    scope.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    scope.addEventListener('input', this.#onInput, { signal });

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // The cart decides the tier too: start from what the server saw and correct it with the real cart
    this.#cartPairs = Number(this.dataset.cartPairs) || 0;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.#refreshCartPairs();

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  /**
   * Asks for the pairs of the cart and redraws when they changed.
   */
  async #refreshCartPairs() {
    const pairs = await fetchCartPairs();
    if (pairs === undefined || !this.isConnected || pairs === this.#cartPairs) return;

    this.#cartPairs = pairs;
    this.#update();
  }

  /**
   * Adding, changing or removing lines changes the pairs of the cart once the request is done.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const { promise } = /** @type {{ promise?: Promise<unknown> }} */ (event);

    if (promise) promise.then(() => this.#refreshCartPairs()).catch(() => {});
    else this.#refreshCartPairs();
  };

  /**
   * A page restored from the back/forward cache shows the cart it had when it was left.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.#refreshCartPairs();
  };

  /**
   * The +/- buttons and the blur of the input announce the new quantity.
   * @param {Event} event
   */
  #onQuantityUpdate = (event) => {
    const quantity = /** @type {CustomEvent<{ quantity?: number }>} */ (event).detail?.quantity;
    this.#update(quantity);
  };

  /**
   * Typing in the quantity input.
   * @param {Event} event
   */
  #onInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') this.#update();
  };

  /**
   * A new variant can change the price, so the tiers come again from the server response.
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    const { promise } = /** @type {{ promise?: Promise<{ detail?: { html?: Document } }> }} */ (event);
    if (!promise) return;

    promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;

        const source = html.querySelector(
          `pc-tiers[data-block-id="${this.dataset.blockId}"][data-part="${this.dataset.part}"]`
        );
        const data = source?.querySelector('[ref="data"]');
        if (!source || !data) return;

        this.refs.data.textContent = data.textContent;

        const rows = source.querySelectorAll('tbody > tr');
        if (rows.length) this.querySelector('tbody')?.replaceChildren(...rows);

        // Rows were replaced: refresh the refs, then redraw the state
        this.updatedCallback();

        // The variant update also redraws the add to cart button: write its label again once that is done
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.#update()));
      })
      .catch(() => {
        // Aborted or failed selection: keep the current state
      });
  };

  /**
   * @returns {TierData | undefined}
   */
  #readData() {
    try {
      const data = JSON.parse(this.refs.data.textContent ?? '');
      return Array.isArray(data?.tiers) && data.tiers.length > 0 ? data : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * @returns {number} The quantity in the input of this product; the last valid one when it is not a number yet.
   */
  #readQuantity() {
    const scope = this.closest('product-component') ?? this.closest('.shopify-section') ?? document;
    const input = scope.querySelector('input[name="quantity"]');
    // Without a quantity input (products that are quoted, not bought) the tiers are shown for one pair
    if (!(input instanceof HTMLInputElement)) return this.#quantity || 1;

    const value = parseInt(input.value, 10);
    return Number.isFinite(value) && value > 0 ? value : this.#quantity;
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} values
   * @returns {string}
   */
  #fill(template, values) {
    return template.replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match));
  }

  /**
   * Money like the server writes it on the card and the table: no trailing zeros, no space after the symbol.
   * @param {number} cents
   */
  #money(cents) {
    const { moneyFormat, currency } = this.dataset;
    return formatMoney(cents, moneyFormat || '{{amount}}', currency || '')
      .replace(/\.00(?!\d)/, '')
      .replace(/^(\D+?)\s+(?=\d)/, (match, symbol) => symbol);
  }

  /**
   * Redraws the active row, the status and the add to cart label for a quantity.
   * @param {number} [quantity] - Defaults to the value of the quantity input.
   */
  #update(quantity) {
    const data = this.#readData();
    if (!data) return;

    const qty = Number.isFinite(quantity) && /** @type {number} */ (quantity) > 0 ? /** @type {number} */ (quantity) : this.#readQuantity();
    if (!qty) return;
    this.#quantity = qty;

    // The discounts count the whole cart: the pairs already in it plus the ones in the input
    const total = qty + this.#cartPairs;

    const { tiers } = data;
    let activeIndex = 0;
    tiers.forEach((tier, index) => {
      if (tier.min <= total) activeIndex = index;
    });
    const active = tiers[activeIndex];
    const next = tiers[activeIndex + 1];

    const { rows = [] } = this.refs;
    rows.forEach((row, index) => {
      const isActive = index === activeIndex;
      row.classList.toggle('pc-tiers__row--active', isActive);
      if (isActive) row.setAttribute('aria-current', 'true');
      else row.removeAttribute('aria-current');
    });

    this.#updateStatus(total, active, next);
    if (this.dataset.addOne) this.#updateAddToCart(qty, active);

    // The "from" price of the product header and the cards of the page follow the selected variant
    if (data.from) {
      for (const element of document.querySelectorAll('[data-pc-from-price]')) {
        if (!(element instanceof HTMLElement) || !element.dataset.template) continue;
        const nextText = this.#fill(element.dataset.template, { price: data.from });
        if (element.textContent !== nextText) element.textContent = nextText;
      }
    }
  }

  /**
   * The message, its colour, the quote link and the bar.
   * @param {number} qty - Pairs the cart would have (the ones in it plus the ones in the input).
   * @param {Tier} active
   * @param {Tier | undefined} next
   */
  #updateStatus(qty, active, next) {
    const { status, message, quoteLink, bar, progress } = this.refs;
    if (!status || !message) return;

    const pct = Math.round(active.pct);
    let state = 'best';
    let text = this.#fill(this.dataset.best ?? '', { pct });

    // Past the last tier (it has a maximum): the order is quoted, not bought by the pair
    if (!next && active.max !== null && qty > active.max) {
      text = this.#fill(this.dataset.over ?? '', { max: active.max });
    }

    if (next) {
      const missing = next.min - qty;
      const one = missing === 1;
      if (active.pct > 0) {
        state = 'applied';
        text = this.#fill((one ? this.dataset.appliedOne : this.dataset.appliedOther) ?? '', { pct, missing, price: next.unit });
      } else {
        state = 'progress';
        text = this.#fill((one ? this.dataset.nextOne : this.dataset.nextOther) ?? '', { missing, price: next.unit });
      }
    }

    if (message.textContent !== text) message.textContent = text;
    status.dataset.state = state;
    if (quoteLink) quoteLink.hidden = Boolean(next);

    // The bar runs from the start of the current tier to the next one, and starts again with each tier
    const base = active.min > 1 ? active.min : 0;
    const percent = next ? Math.max(0, Math.min(100, Math.round(((qty - base) / (next.min - base)) * 100))) : 100;
    if (bar) bar.style.width = `${percent}%`;
    progress?.setAttribute('aria-valuenow', String(percent));
  }

  /**
   * "Agregar al carrito · 12 pares · $6,624": the label is the text of the button, so only that text is replaced and the
   * icon and the "added" animation stay as they are. A disabled button ("Agotado") keeps its own text.
   * @param {number} qty
   * @param {Tier} active
   */
  #updateAddToCart(qty, active) {
    const scope = this.closest('product-component') ?? this.closest('.shopify-section') ?? document;
    const button = scope.querySelector('product-form-component [ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const template = (qty === 1 ? this.dataset.addOne : this.dataset.addOther) ?? '';
    const text = this.#fill(template, { count: qty, total: this.#money(active.cents * qty) });

    for (const node of button.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim()) {
        if (node.nodeValue !== text) node.nodeValue = text;
        return;
      }
    }
  }
}

if (!customElements.get('pc-tiers')) {
  customElements.define('pc-tiers', PcTiers);
}
